import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSyncedStore } from '@syncedstore/react'
import Chip from '../../chip';
import { Icon } from '@iconify/react';
import { X } from 'lucide-react';
import RevertWorkerModal from '../../revert-worker-modal';
import RecordTimeModal from '../../record-time-modal';
import { todoStore, addWorkerTimeLog, softDeleteWorkerTimeLog } from 'src/store/todo';
import type { WorkerAssignment, TaskType, WorkerTimeLog } from 'src/store/todo/types';

type Props = {
  first_name: string;
  last_name:string;
  role:string;
  status:string;
  workerId?: string;
  teamId?: string;
  onWorkerOut?: () => void;
  onRevert?: () => void;
  isTimeclockMode?: boolean;
}

const WorkerCard: React.FC<Props> = ({ last_name, status, first_name, onWorkerOut, onRevert, workerId, teamId, isTimeclockMode = false }) => {
  const navigate = useNavigate()
  const state = useSyncedStore(todoStore)
  const [showRevertModal, setShowRevertModal] = useState(false)
  const [showTimeModal, setShowTimeModal] = useState(false)
  const [selectedTaskTypeId, setSelectedTaskTypeId] = useState<string | null>(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deleteLogId, setDeleteLogId] = useState<string | null>(null)

  // Get worker assignments and task types
  const workerAssignments = workerId
    ? state.workerAssignments.filter((a: WorkerAssignment) =>
        a.workerId === workerId && !a.softDeleted
      )
    : []

  const taskTypesForWorker = workerAssignments.map((a: WorkerAssignment) => {
    const taskType = state.taskTypes.find((t: TaskType) => t.id === a.taskTypeId)
    return taskType
  }).filter((t): t is TaskType => t !== undefined)

  // Get unique task types (a worker might be assigned multiple zones for same task type)
  const uniqueTaskTypes = taskTypesForWorker.filter((t, index, self) =>
    index === self.findIndex((tt) => tt.id === t.id)
  )

  // Get time logs for this worker
  const workerTimeLogs = workerId
    ? state.workerTimeLogs.filter((log: WorkerTimeLog) =>
        log.workerId === workerId && !log.softDeleted
      )
    : []

  // Check if worker has any time logged
  const hasTimeLogged = workerTimeLogs.length > 0

  // Calculate total time logged for this worker
  const totalTimeLogged = workerTimeLogs.reduce((sum, log) => sum + log.value, 0)

  const handlePencilClick = () => {
    if (status === 'Out') {
      setShowRevertModal(true);
    } else if (status === 'Assigned') {
      // Edit existing assignments
      if (workerId && teamId) {
        navigate(`/team/${teamId}/worker/${workerId}/assign?name=${encodeURIComponent(`${first_name} ${last_name}`)}&edit=true`)
      }
    }
  };

  const handleRevert = () => {
    if (onRevert) {
      onRevert();
      setShowRevertModal(false);
    }
  };

  const handleAssignClick = () => {
    if (workerId && teamId) {
      navigate(`/team/${teamId}/worker/${workerId}/assign?name=${encodeURIComponent(`${first_name} ${last_name}`)}`)
    }
  };

  const handleRecordTimeClick = () => {
    // Always pre-select the first task type
    if (uniqueTaskTypes.length > 0) {
      setSelectedTaskTypeId(uniqueTaskTypes[0].id)
    }
    setShowTimeModal(true)
  };

  const handleTaskTypeSelect = (taskTypeId: string) => {
    setSelectedTaskTypeId(taskTypeId)
  };

  const handleSaveTime = (value: number) => {
    if (workerId && selectedTaskTypeId) {
      addWorkerTimeLog(workerId, selectedTaskTypeId, value)
      setShowTimeModal(false)
      setSelectedTaskTypeId(null)
    }
  };

  const handleDeleteTimeLogClick = (logId: string) => {
    setDeleteLogId(logId)
    setShowDeleteConfirm(true)
  };

  const handleConfirmDelete = () => {
    if (deleteLogId) {
      softDeleteWorkerTimeLog(deleteLogId)
      setDeleteLogId(null)
      setShowDeleteConfirm(false)
    }
  };

  const formatTime = (totalSeconds: number): string => {
    const hours = Math.floor(totalSeconds / 3600)
    const minutes = Math.floor((totalSeconds % 3600) / 60)
    return `${hours}h ${minutes.toString().padStart(2, '0')}m`
  };

  ;

  return (
    <>
      <div className='border border-gray-200 bg-white rounded-lg w-full shadow-sm'>
        {/* Worker Info Section */}
        <div className='gap-2 flex p-1.5 items-center'>
          <div className='w-10 h-10 font-medium flex items-center justify-center bg-gray-200 rounded-lg'>
            {`${first_name?.[0]}${last_name?.[0]}`}
          </div>
          <div className='flex-1'>
            <div className='flex flex-col gap-1 justify-between'>
              <div className='flex items-center gap-2'>
                <h1 className='text-sm font-medium'>{first_name} {last_name}</h1>
                {isTimeclockMode && totalTimeLogged > 0 && (
                  <span className='text-xs font-semibold text-gray-600'>
                    {formatTime(totalTimeLogged)}
                  </span>
                )}
              </div>
              {!isTimeclockMode && (
                <div className='flex items-center gap-1'>
                  <Chip
                    label={status}
                    variant={status === 'Assigned' ? 'success' : status==='Out' ? 'error': 'default'}
                  />
                </div>
              )}
            </div>
          </div>
          {isTimeclockMode ? (
            // Timeclock mode: Show Record Time button for assigned workers
            status === 'Assigned' && (
              <button
                onClick={handleRecordTimeClick}
                className={`p-3 text-xs font-medium rounded-md transition-colors flex items-center gap-1 cursor-pointer ${
                  hasTimeLogged
                    ? 'text-green-700 bg-green-200 hover:bg-green-100'
                    : 'text-gray-500 bg-gray-200 hover:bg-gray-300'
                }`}
                title='Record time'
              >
                <Icon icon="lucide:clock" width={24} />
              </button>
            )
          ) : (
            // Normal mode: Show regular buttons
            <div className='flex items-center gap-1'>
              {status === 'Out' || status === 'Assigned' ? (
                <>
                  {/* Time icon button - only show for assigned workers */}
                  {status === 'Assigned' && (
                    <button
                      onClick={handleRecordTimeClick}
                      className={`p-3 text-xs font-medium rounded-md transition-colors flex items-center gap-1 cursor-pointer ${
                        hasTimeLogged
                          ? 'text-green-700 bg-green-200 hover:bg-green-100'
                          : 'text-gray-500 bg-gray-200 hover:bg-gray-300'
                      }`}
                      title='Record time'
                    >
                      <Icon icon="lucide:clock" width={24} />
                    </button>
                  )}
                  <button
                    onClick={handlePencilClick}
                    className='p-3 text-xs font-medium text-green-700 bg-green-200 hover:bg-green-100 rounded-md transition-colors flex items-center gap-1 cursor-pointer'
                    title={status === 'Out' ? 'Revert worker' : 'Edit assignments'}
                  >
                    <Icon icon="lucide:user-round-pen" width={24} />
                  </button>
                </>
              ) : (
                <>
                  {onWorkerOut && (
                    <button
                      onClick={onWorkerOut}
                      className='px-3 py-4 text-xs font-medium text-red-700 bg-red-200 hover:bg-red-200 rounded-md transition-colors'
                    >
                      Out
                    </button>
                  )}
                  <button
                    onClick={handleAssignClick}
                    className='px-3 py-4 text-xs font-medium text-white bg-green-700 hover:bg-green-800 rounded-md transition-colors'
                  >
                    Assign
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        {/* Time Logs Section - Only show in timeclock mode if worker has logged time */}
        {isTimeclockMode && status === 'Assigned' && workerTimeLogs.length > 0 && (
          <div className='border-t border-gray-200 px-3 py-1'>
            <div className='flex flex-col gap-0.5'>
              {uniqueTaskTypes.map((taskType) => {
                const logsForTask = workerTimeLogs.filter(
                  (log: WorkerTimeLog) => log.taskTypeId === taskType.id
                )

                if (logsForTask.length === 0) return null

                // Calculate total for this task type (same as Analytics page)
                const assignmentLogsForTask = workerId
                  ? state.workerAssignmentLogs.filter(
                      (log: any) =>
                        log.workerId === workerId &&
                        log.taskTypeId === taskType.id &&
                        !log.softDeleted
                    )
                  : []

                // For harvest tasks (HVST), sum the values (boxes)
                // For other tasks, count the logs (completed locations)
                const total = taskType.taskTypeMoniker === 'HVST'
                  ? assignmentLogsForTask.reduce((sum: number, log: any) => sum + log.value, 0)
                  : assignmentLogsForTask.length

                const [firstLog, ...remainingLogs] = logsForTask

                return (
                  <div key={taskType.id} className='flex flex-col gap-0.5'>
                    {/* First log with task type - time comes first */}
                    <div className='flex items-center justify-between bg-gray-50 rounded px-1.5 py-0 text-sm'>
                      <div className='flex items-center gap-2'>
                        <span className='font-medium text-gray-700 min-w-[60px]'>
                          {formatTime(firstLog.value)}
                        </span>
                        <h4 className='font-semibold text-gray-700'>
                          {taskType.taskType} ({total})
                        </h4>
                      </div>
                      <button
                        onClick={() => handleDeleteTimeLogClick(firstLog.id)}
                        className='p-0.5 text-red-600 hover:bg-red-50 rounded transition-colors'
                        title='Delete time log'
                      >
                        <X size={14} />
                      </button>
                    </div>

                    {/* Remaining logs below - no indentation, no task type */}
                    {remainingLogs.map((log: WorkerTimeLog) => (
                      <div
                        key={log.id}
                        className='flex items-center justify-between bg-gray-50 rounded px-1.5 py-0 text-sm'
                      >
                        <span className='font-medium text-gray-700 min-w-[60px]'>
                          {formatTime(log.value)}
                        </span>
                        <button
                          onClick={() => handleDeleteTimeLogClick(log.id)}
                          className='p-0.5 text-red-600 hover:bg-red-50 rounded transition-colors'
                          title='Delete time log'
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )
              })}
            </div>
          </div>
        )}
      </div>

      <RevertWorkerModal
        open={showRevertModal}
        workerName={`${first_name} ${last_name}`}
        onRevert={handleRevert}
        onCancel={() => setShowRevertModal(false)}
      />

      {/* Time Recording Modal */}
      <RecordTimeModal
        isOpen={showTimeModal}
        title={`Record Time - ${first_name} ${last_name}`}
        taskTypes={uniqueTaskTypes.map(t => {
          // Calculate total for this task type
          const logsForTask = workerId
            ? state.workerAssignmentLogs.filter(
                (log: any) =>
                  log.workerId === workerId &&
                  log.taskTypeId === t.id &&
                  !log.softDeleted
              )
            : []

          // For harvest tasks (HVST), sum the values (boxes)
          // For other tasks, count the logs (completed locations)
          const total = t.taskTypeMoniker === 'HVST'
            ? logsForTask.reduce((sum: number, log: any) => sum + log.value, 0)
            : logsForTask.length

          return { id: t.id, taskType: t.taskType, total }
        })}
        selectedTaskTypeId={selectedTaskTypeId}
        onTaskTypeSelect={handleTaskTypeSelect}
        onSave={handleSaveTime}
        onCancel={() => {
          setShowTimeModal(false)
          setSelectedTaskTypeId(null)
        }}
      />

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'>
          <div className='bg-white rounded-lg p-6 max-w-sm mx-4 shadow-xl'>
            <h3 className='text-lg font-semibold mb-3'>Delete Time Entry?</h3>
            <p className='text-gray-600 mb-6'>Are you sure you want to delete this time entry? This action cannot be undone.</p>
            <div className='flex gap-3 justify-end'>
              <button
                onClick={() => {
                  setShowDeleteConfirm(false)
                  setDeleteLogId(null)
                }}
                className='px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors'
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                className='px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors'
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
export default WorkerCard
